import { G, Svg } from '@svgdotjs/svg.js';
import { StageConstants } from 'core/constants';

interface Bounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface SafeAreaFrame {
  parentToRoot: DOMMatrix;
  safeArea: Bounds;
  overshoot: Bounds;
}

const AREA_TOLERANCE = 1e-6;
const TOUCH_TOLERANCE = 2e-3;
const SAFE_AREA_HINT_DURATION = 2000; /* ms */

export class SafeArea
{
  private hint: G | undefined = undefined;
  private hintTimeout: any = undefined;

  constructor(private wrapper: Svg)
  { }

  private get frame(): SafeAreaFrame | undefined
  {
    const root = this.wrapper.root();
    const parentToScreen =
        (this.wrapper.parent().node as SVGGraphicsElement).getScreenCTM();
    const rootToScreen = root.node.getScreenCTM();

    if (!parentToScreen || !rootToScreen) {
      return undefined;
    }

    const viewbox = root.viewbox();
    const margin = StageConstants.SAFE_AREA_MARGIN_FACTOR *
        Math.min(viewbox.width, viewbox.height);
    const safe = {
      left: viewbox.x + margin,
      top: viewbox.y + margin,
      right: viewbox.x + viewbox.width - margin,
      bottom: viewbox.y + viewbox.height - margin,
    };

    const parentToRoot = rootToScreen.inverse().multiply(parentToScreen);
    const x = this.wrapper.x() as number;
    const y = this.wrapper.y() as number;
    const width = this.wrapper.width() as number;
    const height = this.wrapper.height() as number;
    const corners =
        [[x, y], [x + width, y], [x, y + height], [x + width, y + height]]
          .map(([px, py]) =>
              new DOMPoint(px, py).matrixTransform(parentToRoot));

    return {
      parentToRoot,
      safeArea: safe,
      overshoot: {
        left: safe.left - Math.min(...corners.map(c => c.x)),
        top: safe.top - Math.min(...corners.map(c => c.y)),
        right: Math.max(...corners.map(c => c.x)) - safe.right,
        bottom: Math.max(...corners.map(c => c.y)) - safe.bottom,
      },
    };
  }

  public get containsWrapper(): boolean
  {
    const frame = this.frame;
    if (!frame) {
      return true;
    }

    const o = frame.overshoot;
    const tolerance =
        AREA_TOLERANCE * (frame.safeArea.right - frame.safeArea.left);

    return Math.max(o.left, o.top, o.right, o.bottom) <= tolerance;
  }

  public holdsWrapperOfSize(
    unitsPerWrapperUnit: number
  ): (size: { width: number; height: number }) => boolean
  {
    const frame = this.frame;
    if (!frame) {
      return () => true;
    }

    const m = frame.parentToRoot;
    const safeWidth = frame.safeArea.right - frame.safeArea.left;
    const safeHeight = frame.safeArea.bottom - frame.safeArea.top;
    const tolerance = AREA_TOLERANCE * safeWidth;

    return (size: { width: number; height: number }): boolean => {
      const width = size.width / unitsPerWrapperUnit;
      const height = size.height / unitsPerWrapperUnit;

      return Math.abs(m.a * width) + Math.abs(m.c * height) <=
              safeWidth + tolerance &&
          Math.abs(m.b * width) + Math.abs(m.d * height) <=
              safeHeight + tolerance;
    };
  }

  public slideWrapperInto(): void
  {
    const frame = this.frame;
    if (!frame) {
      return;
    }

    const o = frame.overshoot;
    const shiftInto = (low: number, high: number): number =>
        low > 0 ? low : (high > 0 ? -high : 0);
    const dx = shiftInto(o.left, o.right);
    const dy = shiftInto(o.top, o.bottom);
    if (dx === 0 && dy === 0) {
      return;
    }

    const toParent = frame.parentToRoot.inverse();
    this.wrapper.move(
        (this.wrapper.x() as number) + toParent.a * dx + toParent.c * dy,
        (this.wrapper.y() as number) + toParent.b * dx + toParent.d * dy);
  }

  public pullWrapperWidthInto(): void
  {
    const frame = this.frame;
    if (!frame) {
      return;
    }

    const o = frame.overshoot;
    const m = frame.parentToRoot;
    let pullLeft = 0;
    let pullRight = 0;

    const pullIn = (
        overshoot: number, rootShiftPerRightBorderUnit: number): void => {
      const shift = rootShiftPerRightBorderUnit;
      if (overshoot <= 0 || Math.abs(shift) < AREA_TOLERANCE) {
        return;
      }
      if (shift > 0) {
        pullRight = Math.max(pullRight, overshoot / shift);
      }
      else {
        pullLeft = Math.max(pullLeft, -overshoot / shift);
      }
    };

    pullIn(o.right, m.a);
    pullIn(o.left, -m.a);
    pullIn(o.bottom, m.b);
    pullIn(o.top, -m.b);

    const width = this.wrapper.width() as number;
    if (pullLeft + pullRight >= width) {
      return;
    }

    this.wrapper
      .size(width - pullLeft - pullRight, this.wrapper.height())
      .move((this.wrapper.x() as number) + pullLeft, this.wrapper.y());
  }

  public applyTouchedSidesHint(isVisible: boolean): void
  {
    if (this.hint) {
      this.hint.remove();
      this.hint = undefined;
    }
    clearTimeout(this.hintTimeout);

    const frame = this.frame;
    if (!isVisible || !frame) {
      return;
    }

    const safe = frame.safeArea;
    const group = this.wrapper.root().group()
      .addClass(StageConstants.CLASS_NAME_SAFE_AREA_HINT);
    this.hint = group;

    const o = frame.overshoot;
    const tolerance = (safe.right - safe.left) * TOUCH_TOLERANCE;
    const touches = (overshoot: number): boolean =>
        Math.abs(overshoot) <= tolerance;

    if (touches(o.left)) {
      group.line(safe.left, safe.top, safe.left, safe.bottom);
    }
    if (touches(o.right)) {
      group.line(safe.right, safe.top, safe.right, safe.bottom);
    }
    if (touches(o.top)) {
      group.line(safe.left, safe.top, safe.right, safe.top);
    }
    if (touches(o.bottom)) {
      group.line(safe.left, safe.bottom, safe.right, safe.bottom);
    }

    this.hintTimeout = setTimeout(
        () => this.applyTouchedSidesHint(false), SAFE_AREA_HINT_DURATION);
  }
}
